import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Search,
  X,
  QrCode,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../components/AdminSettings";
import RichTextEditor from "../../components/RichTextEditor";

import { API_URL } from "../../config";
import { suggestEmailFix } from "../../lib/emailTypo";

export const EditSaleModal = ({ sale, sessions, showData, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    church: "",
    age: "",
    birth_date: "",
    is_vip: false,
    is_first_time_override: null, // null = auto-detect; true = force first time; false = force off
    is_birthday_override: null, // null = auto (via birth_date); true = force; false = force off
    group_code: "",
    payment_status: "",
    session_id: "",
    ticket_type_id: "",
    coupon_code: "",
    checked_in: false,
    final_price: "",
    amount_paid: "",
    payment_method: "",
  });
  const [saving, setSaving] = useState(false);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [otherSelected, setOtherSelected] = useState({}); // for dropdowns with allow_other
  const [showExtraFields, setShowExtraFields] = useState(false); // collapsed by default

  // Default seeded fields — already editable via standard inputs above. Excluded from "Campos Personalizados" section.
  const DEFAULT_FIELD_NAMES = new Set(["first_name", "last_name", "email", "phone", "church", "age"]);
  const extraFields = customFields.filter((f) => !DEFAULT_FIELD_NAMES.has(f.name));

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/custom-fields`, { withCredentials: true })
      .then((r) => setCustomFields(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCustomFields([]));
  }, []);

  // Sync custom values with current sale + detect "Outro" usage for dropdowns
  useEffect(() => {
    if (!sale) return;
    const saved = sale.custom_fields || {};
    setCustomValues({ ...saved });
    const otherMap = {};
    customFields.forEach((cf) => {
      if (cf.field_type === "dropdown" && cf.allow_other) {
        const v = saved[cf.name];
        // Mark as "Outro" if saved value exists but is not in the configured options.
        if (v && !(cf.options || []).includes(v)) otherMap[cf.name] = true;
      }
    });
    // Same logic for default-field dropdowns (church, phone, age) when configured as dropdown.
    ["church", "phone", "age"].forEach((name) => {
      const cf = customFields.find((c) => c.name === name);
      if (cf && cf.field_type === "dropdown" && cf.allow_other) {
        const v = name === "age" ? (sale.age != null ? String(sale.age) : "") : (sale[name] || "");
        if (v && !(cf.options || []).includes(v)) otherMap[name] = true;
      }
    });
    setOtherSelected(otherMap);
  }, [sale, customFields]);

  useEffect(() => {
    if (sale) {
      setFormData({
        first_name: sale.first_name || "",
        last_name: sale.last_name || "",
        email: sale.email || "",
        phone: sale.phone || "",
        church: sale.church || "",
        age: sale.age?.toString() || "",
        birth_date: sale.birth_date || "",
        is_vip: !!sale.is_vip,
        is_first_time_override: sale.is_first_time_override === undefined ? null : sale.is_first_time_override,
        is_birthday_override: sale.is_birthday_override === undefined ? null : sale.is_birthday_override,
        group_code: sale.group_code || "",
        payment_status: sale.payment_status || "pending",
        session_id: sale.session_id || "",
        ticket_type_id: sale.ticket_type_id || "",
        coupon_code: sale.coupon_code || "",
        checked_in: sale.checked_in || false,
        final_price: sale.final_price?.toString() || "0",
        amount_paid: sale.amount_paid?.toString() || (sale.payment_status === "paid" ? (sale.final_price?.toString() || "0") : "0"),
        payment_method: sale.payment_method || "",
      });
    }
  }, [sale]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/groups`, { withCredentials: true })
      .then(r => setGroups(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (formData.session_id) {
      axios.get(`${API_URL}/api/ticket-types/${formData.session_id}`)
        .then(res => setTicketTypes(res.data))
        .catch(() => setTicketTypes([]));
    }
  }, [formData.session_id]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/coupons`, { withCredentials: true })
      .then(res => setCoupons(res.data))
      .catch(() => setCoupons([]));
  }, []);

  // Recalculate price when coupon or ticket type changes
  useEffect(() => {
    const tt = ticketTypes.find(t => t.id === formData.ticket_type_id);
    const ticketPrice = tt ? tt.price : (sale?.original_price || 0);
    let discount = 0;
    if (formData.coupon_code) {
      const coupon = coupons.find(c => c.code === formData.coupon_code.toUpperCase() && c.active);
      if (coupon) {
        if (coupon.discount_type === "percentage") {
          discount = ticketPrice * coupon.discount_value / 100;
        } else {
          discount = Math.min(coupon.discount_value, ticketPrice);
        }
      }
    }
    const newFinalPrice = Math.max(0, ticketPrice - discount).toFixed(2);
    setFormData(prev => ({ ...prev, final_price: newFinalPrice }));
  }, [formData.coupon_code, formData.ticket_type_id, ticketTypes, coupons, sale]);

  const handleSave = async (opts = {}) => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        age: parseInt(formData.age),
        final_price: parseFloat(formData.final_price),
        amount_paid: parseFloat(formData.amount_paid),
        payment_method: formData.payment_method || null,
      };
      if (extraFields.length > 0) {
        // Only send non-default custom field values (defaults are already in formData)
        payload.custom_fields = Object.fromEntries(extraFields.map((cf) => [cf.name, customValues[cf.name] ?? ""]));
      }
      // When the ticket type changes AND the plan is on installments, apply
      // the recalculated schedule (only if the admin confirmed via dialog).
      if (opts.payment_schedule) {
        payload.payment_schedule = opts.payment_schedule;
      }
      await axios.put(
        `${API_URL}/api/admin/sales/${sale.id}`,
        payload,
        { withCredentials: true }
      );
      toast.success("Venda atualizada com sucesso!");
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar venda");
    } finally {
      setSaving(false);
    }
  };

  // ─── Recalc installment schedule when ticket type / price changes ────
  const [recalcDialog, setRecalcDialog] = useState(null); // {new_final, paid, delta, new_schedule, refund_needed}
  const buildRecalcPreview = () => {
    const oldSchedule = Array.isArray(sale?.payment_schedule) ? sale.payment_schedule : [];
    if (oldSchedule.length === 0) return null;
    const paid = oldSchedule.filter((i) => i.paid).reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const newFinal = parseFloat(formData.final_price) || 0;
    const remaining = Math.max(0, newFinal - paid);
    const unpaid = oldSchedule.filter((i) => !i.paid);
    let new_schedule = null;
    if (newFinal >= paid && unpaid.length > 0) {
      const per = Math.round((remaining / unpaid.length) * 100) / 100;
      // distribute rounding leftover on the last unpaid item
      const rebuilt = oldSchedule.map((i) => ({ ...i }));
      let assigned = 0;
      let unpaidCount = 0;
      const unpaidCountTotal = unpaid.length;
      for (const item of rebuilt) {
        if (item.paid) continue;
        unpaidCount += 1;
        if (unpaidCount === unpaidCountTotal) {
          item.amount = Math.round((remaining - assigned) * 100) / 100;
        } else {
          item.amount = per;
          assigned = Math.round((assigned + per) * 100) / 100;
        }
      }
      new_schedule = rebuilt;
    }
    return {
      new_final: newFinal,
      paid,
      remaining,
      old_total: oldSchedule.reduce((s, i) => s + (Number(i.amount) || 0), 0),
      unpaid_count: unpaid.length,
      new_schedule,
      refund_needed: newFinal < paid ? paid - newFinal : 0,
    };
  };

  const onClickSave = () => {
    const ticketTypeChanged = sale && formData.ticket_type_id !== (sale.ticket_type_id || "");
    const oldFinal = Number(sale?.final_price ?? 0);
    const newFinal = Number(formData.final_price ?? 0);
    const finalChanged = Math.abs(newFinal - oldFinal) > 0.001;
    const hasInstallments = Array.isArray(sale?.payment_schedule) && sale.payment_schedule.length > 0;
    if ((ticketTypeChanged || finalChanged) && hasInstallments) {
      const preview = buildRecalcPreview();
      if (preview) {
        setRecalcDialog(preview);
        return;
      }
    }
    handleSave();
  };

  if (!isOpen || !sale) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-h-[90vh] overflow-y-auto max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-white">Editar Bilhete</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="bg-zinc-800/50 p-3 rounded-lg">
            <p className="text-zinc-500 text-sm">Número do Bilhete</p>
            <p className="text-amber-500 font-mono font-bold">{sale.ticket_number}</p>
          </div>
          <div>
            <Label className="text-zinc-300">Sessão</Label>
            <Select
              value={formData.session_id}
              onValueChange={(value) => setFormData({ ...formData, session_id: value })}
            >
              <SelectTrigger className="input-dark mt-2" data-testid="edit-sale-session">
                <SelectValue placeholder="Selecionar sessão" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {sessions.map((session) => {
                  const parts = [];
                  if (session.name) parts.push(session.name);
                  if (session.subtitle) parts.push(session.subtitle);
                  if (session.date) parts.push(session.date);
                  if (session.time) parts.push(session.time);
                  const label = parts.join(" · ") || "(sessão sem nome)";
                  return (
                    <SelectItem key={session.id} value={session.id} className="text-white">
                      {label} ({session.available_seats} lugares)
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300">Tipo de Bilhete</Label>
            <Select
              value={formData.ticket_type_id}
              onValueChange={(value) => setFormData({ ...formData, ticket_type_id: value })}
            >
              <SelectTrigger className="input-dark mt-2" data-testid="edit-sale-ticket-type">
                <SelectValue placeholder="Selecionar tipo de bilhete" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {ticketTypes.map((tt) => (
                  <SelectItem key={tt.id} value={tt.id} className="text-white">
                    {tt.name} - {tt.price.toFixed(2)}€
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300">Primeiro Nome</Label>
            <Input
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              className="input-dark mt-2"
              data-testid="edit-sale-first-name"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Último Nome</Label>
            <Input
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              className="input-dark mt-2"
              data-testid="edit-sale-last-name"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input-dark mt-2"
              data-testid="edit-sale-email"
            />
            {(() => {
              const s = suggestEmailFix(formData.email);
              if (!s) return null;
              return (
                <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100"
                     data-testid="edit-sale-email-suggestion">
                  <span>Quis dizer <strong className="font-semibold text-amber-50">{s.suggestion}</strong>?</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, email: s.suggestion })}
                    className="rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-black hover:bg-amber-400 transition-colors"
                    data-testid="edit-sale-email-apply"
                  >Aplicar</button>
                </div>
              );
            })()}
          </div>
          <div>
            <Label className="text-zinc-300">Telefone</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input-dark mt-2"
              data-testid="edit-sale-phone"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Igreja</Label>
            {(() => {
              const cf = customFields.find((c) => c.name === "church");
              const isOther = !!otherSelected.church;
              if (cf && cf.field_type === "dropdown" && Array.isArray(cf.options) && cf.options.length > 0) {
                return (
                  <>
                    <Select
                      value={isOther ? "__OTHER__" : (formData.church || "")}
                      onValueChange={(v) => {
                        if (v === "__OTHER__") {
                          setOtherSelected((p) => ({ ...p, church: true }));
                          setFormData({ ...formData, church: "" });
                        } else {
                          setOtherSelected((p) => ({ ...p, church: false }));
                          setFormData({ ...formData, church: v });
                        }
                      }}
                    >
                      <SelectTrigger className="input-dark mt-2" data-testid="edit-sale-church-select">
                        <SelectValue placeholder={cf.placeholder || "Selecione…"} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        {cf.options.map((opt) => (
                          <SelectItem key={opt} value={opt} className="text-white">{opt}</SelectItem>
                        ))}
                        {cf.allow_other && (
                          <SelectItem value="__OTHER__" className="text-amber-300">Outro</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {isOther && (
                      <Input
                        value={formData.church}
                        onChange={(e) => setFormData({ ...formData, church: e.target.value })}
                        placeholder="Especifique a igreja"
                        className="input-dark mt-2"
                        data-testid="edit-sale-church-other"
                      />
                    )}
                  </>
                );
              }
              return (
                <Input
                  value={formData.church}
                  onChange={(e) => setFormData({ ...formData, church: e.target.value })}
                  className="input-dark mt-2"
                  data-testid="edit-sale-church"
                />
              );
            })()}
          </div>
          <div>
            <Label className="text-zinc-300">Idade</Label>
            {(() => {
              const cf = customFields.find((c) => c.name === "age");
              const isOther = !!otherSelected.age;
              if (cf && cf.field_type === "dropdown" && Array.isArray(cf.options) && cf.options.length > 0) {
                return (
                  <>
                    <Select
                      value={isOther ? "__OTHER__" : (formData.age || "")}
                      onValueChange={(v) => {
                        if (v === "__OTHER__") {
                          setOtherSelected((p) => ({ ...p, age: true }));
                          setFormData({ ...formData, age: "" });
                        } else {
                          setOtherSelected((p) => ({ ...p, age: false }));
                          setFormData({ ...formData, age: v });
                        }
                      }}
                    >
                      <SelectTrigger className="input-dark mt-2" data-testid="edit-sale-age-select">
                        <SelectValue placeholder={cf.placeholder || "Selecione…"} />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        {cf.options.map((opt) => (
                          <SelectItem key={opt} value={opt} className="text-white">{opt}</SelectItem>
                        ))}
                        {cf.allow_other && (
                          <SelectItem value="__OTHER__" className="text-amber-300">Outro</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {isOther && (
                      <Input
                        value={formData.age}
                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        placeholder="Especifique"
                        className="input-dark mt-2"
                        data-testid="edit-sale-age-other"
                      />
                    )}
                  </>
                );
              }
              return (
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  className="input-dark mt-2"
                  data-testid="edit-sale-age"
                />
              );
            })()}
          </div>
          <div>
            <Label className="text-zinc-300">Data de Nascimento</Label>
            <Input
              type="date"
              value={formData.birth_date}
              onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
              className="input-dark mt-2"
              data-testid="edit-sale-birthdate"
            />
            <p className="text-zinc-500 text-xs mt-1">Usada para destacar aniversariantes no ecrã de check-in.</p>
          </div>
          <div>
            <Label className="text-zinc-300">Grupo</Label>
            <Select
              value={formData.group_code || "none"}
              onValueChange={(value) => setFormData({ ...formData, group_code: value === "none" ? "" : value })}
            >
              <SelectTrigger className="input-dark mt-2" data-testid="edit-sale-group">
                <SelectValue placeholder="Sem grupo" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="none" className="text-white">Sem grupo</SelectItem>
                {groups.filter(g => g.active).map((g) => (
                  <SelectItem key={g.code} value={g.code} className="text-white">
                    {g.name} ({g.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-3 pb-2">
            <Switch
              checked={formData.is_vip}
              onCheckedChange={(v) => setFormData({ ...formData, is_vip: v })}
              data-testid="edit-sale-vip"
            />
            <span className="text-zinc-300 text-sm">⭐ VIP / Convidado Especial</span>
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300">Ecrã Check-In · Variante "🎂 Aniversário"</Label>
            <Select
              value={
                formData.is_birthday_override === true ? "force_on"
                : formData.is_birthday_override === false ? "force_off"
                : "auto"
              }
              onValueChange={(v) => setFormData({
                ...formData,
                is_birthday_override: v === "force_on" ? true : v === "force_off" ? false : null,
              })}
            >
              <SelectTrigger className="input-dark mt-2" data-testid="edit-sale-birthday">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="auto" className="text-white">Auto (detectar pela data de nascimento)</SelectItem>
                <SelectItem value="force_on" className="text-white">🎂 Forçar "Aniversário" — animação especial</SelectItem>
                <SelectItem value="force_off" className="text-white">Desactivar — animação normal</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-zinc-500 text-xs mt-1">
              "Auto" usa a data de nascimento do bilhete. Pode forçar sem precisar de editar a data — útil para festas surpresa ou aniversários atrasados/antecipados.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300">Ecrã Check-In · Variante "1ª Vez"</Label>
            <Select
              value={
                formData.is_first_time_override === true ? "force_on"
                : formData.is_first_time_override === false ? "force_off"
                : "auto"
              }
              onValueChange={(v) => setFormData({
                ...formData,
                is_first_time_override: v === "force_on" ? true : v === "force_off" ? false : null,
              })}
            >
              <SelectTrigger className="input-dark mt-2" data-testid="edit-sale-first-time">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="auto" className="text-white">Auto (detectar por email)</SelectItem>
                <SelectItem value="force_on" className="text-white">✨ Forçar "1ª Vez" — animação especial</SelectItem>
                <SelectItem value="force_off" className="text-white">Desactivar — animação normal</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-zinc-500 text-xs mt-1">
              "Auto" usa o histórico de check-ins do email. Pode forçar manualmente para convidados especiais ou impedir a animação em casos específicos.
            </p>
          </div>
          <div>
            <Label className="text-zinc-300">Cupão de Desconto</Label>
            <Select
              value={formData.coupon_code || "none"}
              onValueChange={(value) => setFormData({ ...formData, coupon_code: value === "none" ? "" : value })}
            >
              <SelectTrigger className="input-dark mt-2" data-testid="edit-sale-coupon">
                <SelectValue placeholder="Sem cupão" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="none" className="text-white">Sem cupão</SelectItem>
                {coupons.filter(c => c.active).map((c) => (
                  <SelectItem key={c.code} value={c.code} className="text-white">
                    {c.code} ({c.discount_type === "percentage" ? `${c.discount_value}%` : `${c.discount_value.toFixed(2)}€`})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(() => {
              const coupon = coupons.find(c => c.code === formData.coupon_code && c.active);
              if (coupon) {
                const tt = ticketTypes.find(t => t.id === formData.ticket_type_id);
                const ticketPrice = tt ? tt.price : (sale?.original_price || 0);
                const discount = coupon.discount_type === "percentage"
                  ? ticketPrice * coupon.discount_value / 100
                  : Math.min(coupon.discount_value, ticketPrice);
                return (
                  <p className="text-green-400 text-xs mt-1">
                    Desconto de {discount.toFixed(2)}€
                  </p>
                );
              }
              return null;
            })()}
          </div>
          <div>
            <Label className="text-zinc-300">Valor a Pagar (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.final_price}
              className="input-dark mt-2 opacity-60"
              disabled
              data-testid="edit-sale-final-price"
            />
            <p className="text-zinc-500 text-xs mt-1">Calculado automaticamente (preço do bilhete - cupão)</p>
          </div>
          <div>
            <Label className="text-zinc-300">Valor Pago (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.amount_paid}
              onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
              className="input-dark mt-2"
              data-testid="edit-sale-amount-paid"
            />
            <p className="text-zinc-500 text-xs mt-1">Valor realmente pago pelo cliente. Pode ajustar manualmente.</p>
          </div>
          <div>
            <Label className="text-zinc-300">Valor em Falta (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={Math.max(0, (parseFloat(formData.final_price) || 0) - (parseFloat(formData.amount_paid) || 0)).toFixed(2)}
              className="input-dark mt-2 opacity-60"
              disabled
              data-testid="edit-sale-amount-due"
            />
            <p className="text-zinc-500 text-xs mt-1">Diferença entre valor a pagar e valor pago</p>
          </div>
          <div>
            <Label className="text-zinc-300">Tipo de Pagamento</Label>
            <Select
              value={formData.payment_method || "none"}
              onValueChange={(value) => setFormData({ ...formData, payment_method: value === "none" ? "" : value })}
            >
              <SelectTrigger className="input-dark mt-2" data-testid="edit-sale-payment-method">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="none" className="text-white">Não definido</SelectItem>
                <SelectItem value="Stripe" className="text-white">Stripe</SelectItem>
                <SelectItem value="Link Externo" className="text-white">{showData?.external_payment_title || "Link de Pagamento Externo"}</SelectItem>
                {(showData?.offline_locations || []).filter(l => l.active !== false).map((loc, i) => (
                  <SelectItem key={`${loc.title}-${i}`} value={loc.title} className="text-white">{loc.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300">Estado do Pagamento</Label>
            <Select
              value={formData.payment_status}
              onValueChange={(value) => setFormData({ ...formData, payment_status: value })}
            >
              <SelectTrigger className="input-dark mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="pending" className="text-white">Pendente</SelectItem>
                <SelectItem value="paid" className="text-white">Pago</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-white/10">
            <div>
              <p className="text-white font-medium text-sm">Scanner / Check-in</p>
              <p className="text-zinc-500 text-xs">{formData.checked_in ? "Bilhete já validado na entrada" : "Bilhete ainda não validado"}</p>
            </div>
            <Switch
              checked={formData.checked_in}
              onCheckedChange={(checked) => setFormData({ ...formData, checked_in: checked })}
              className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-zinc-600"
              data-testid="edit-sale-checkin"
            />
          </div>

          {extraFields.length > 0 && (
            <div className="rounded-lg bg-violet-500/5 border border-violet-500/20" data-testid="edit-sale-custom-fields">
              <button
                type="button"
                onClick={() => setShowExtraFields((v) => !v)}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-violet-500/10 transition-colors rounded-lg"
                data-testid="edit-sale-cf-toggle"
                aria-expanded={showExtraFields}
              >
                <span className="text-violet-300 text-xs uppercase tracking-wider font-semibold">
                  Campos Personalizados ({extraFields.length})
                </span>
                <ChevronDown className={`w-4 h-4 text-violet-300 transition-transform ${showExtraFields ? "rotate-180" : ""}`} />
              </button>
              {showExtraFields && (
                <div className="space-y-3 p-3 pt-0">
              {extraFields.map((cf) => {
                const v = customValues[cf.name] ?? "";
                const set = (val) => setCustomValues((p) => ({ ...p, [cf.name]: val }));
                const isOther = !!otherSelected[cf.name];
                return (
                  <div key={cf.id}>
                    <Label className="text-zinc-300 text-xs">
                      {cf.label}
                      {cf.required && <span className="text-red-400 ml-1">*</span>}
                    </Label>
                    {cf.field_type === "text" && (
                      <Input value={v} onChange={(e) => set(e.target.value)} className="input-dark mt-1" data-testid={`edit-cf-${cf.name}`} />
                    )}
                    {cf.field_type === "email" && (
                      <Input type="email" value={v} onChange={(e) => set(e.target.value)} className="input-dark mt-1" data-testid={`edit-cf-${cf.name}`} />
                    )}
                    {cf.field_type === "phone" && (
                      <Input type="tel" value={v} onChange={(e) => set(e.target.value)} className="input-dark mt-1" data-testid={`edit-cf-${cf.name}`} />
                    )}
                    {cf.field_type === "number" && (
                      <Input type="number" value={v} onChange={(e) => set(e.target.value)} className="input-dark mt-1" data-testid={`edit-cf-${cf.name}`} />
                    )}
                    {cf.field_type === "date" && (
                      <Input type="date" value={v || ""} max={new Date().toISOString().slice(0, 10)} onChange={(e) => set(e.target.value)} className="input-dark mt-1" data-testid={`edit-cf-${cf.name}`} />
                    )}
                    {cf.field_type === "checkbox" && (
                      <div className="flex items-center gap-3 mt-1">
                        <Switch
                          checked={!!v}
                          onCheckedChange={(c) => set(c)}
                          className="data-[state=checked]:bg-violet-500"
                          data-testid={`edit-cf-${cf.name}`}
                        />
                        <span className="text-zinc-400 text-sm">{v ? "Sim" : "Não"}</span>
                      </div>
                    )}
                    {cf.field_type === "dropdown" && (
                      <>
                        <Select
                          value={isOther ? "__OTHER__" : (v || "")}
                          onValueChange={(val) => {
                            if (val === "__OTHER__") {
                              setOtherSelected((p) => ({ ...p, [cf.name]: true }));
                              set("");
                            } else {
                              setOtherSelected((p) => ({ ...p, [cf.name]: false }));
                              set(val);
                            }
                          }}
                        >
                          <SelectTrigger className="input-dark mt-1" data-testid={`edit-cf-${cf.name}`}>
                            <SelectValue placeholder={cf.placeholder || "Selecione…"} />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10">
                            {(cf.options || []).map((opt) => (
                              <SelectItem key={opt} value={opt} className="text-white">{opt}</SelectItem>
                            ))}
                            {cf.allow_other && (
                              <SelectItem value="__OTHER__" className="text-amber-300">Outro</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        {isOther && (
                          <Input
                            value={v}
                            onChange={(e) => set(e.target.value)}
                            placeholder="Especifique a sua resposta"
                            className="input-dark mt-2"
                            data-testid={`edit-cf-${cf.name}-other`}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button onClick={onClose} variant="outline" className="flex-1 btn-secondary">
              Cancelar
            </Button>
            <Button onClick={onClickSave} disabled={saving} className="flex-1 btn-primary" data-testid="save-edit-btn">
              {saving ? "A guardar..." : "Guardar"}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Recalc de prestações ao mudar o tipo de bilhete */}
      {recalcDialog && (
        <AlertDialog open={!!recalcDialog} onOpenChange={(o) => !o && setRecalcDialog(null)}>
          <AlertDialogContent className="bg-zinc-900 border-white/10 text-white max-w-lg" data-testid="recalc-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Recalcular plano de prestações?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400 space-y-2">
                <span className="block">O tipo de bilhete mudou. As prestações actuais NÃO batem com o novo valor.</span>
                <span className="block bg-white/5 border border-white/10 rounded p-3 text-xs font-mono">
                  <span className="block">Novo valor final:  <span className="text-amber-300">{recalcDialog.new_final.toFixed(2)}€</span></span>
                  <span className="block">Já pago em prestações:  <span className="text-emerald-300">{recalcDialog.paid.toFixed(2)}€</span></span>
                  <span className="block">Em falta a distribuir:  <span className="text-sky-300">{recalcDialog.remaining.toFixed(2)}€</span> por {recalcDialog.unpaid_count} prestação(ões)</span>
                </span>
                {recalcDialog.refund_needed > 0 ? (
                  <span className="block bg-red-500/10 border border-red-500/30 rounded p-3 text-xs">
                    ⚠️ O novo valor é <strong>inferior</strong> ao já pago. Existem <strong>{recalcDialog.refund_needed.toFixed(2)}€</strong> a devolver ao comprador.
                    <br />O plano NÃO será recalculado automaticamente. Grava a mudança de tipo e depois cria uma Devolução manualmente em Finanças → Devoluções.
                  </span>
                ) : recalcDialog.new_schedule ? (
                  <span className="block">
                    As {recalcDialog.unpaid_count} prestação(ões) por pagar serão distribuídas de forma proporcional:
                    <span className="block mt-2 font-mono text-xs bg-white/5 border border-white/10 rounded p-2 max-h-40 overflow-y-auto">
                      {recalcDialog.new_schedule.filter((i) => !i.paid).map((i, idx) => (
                        <span key={idx} className="block">{i.due_date || `#${idx + 1}`}  →  <span className="text-amber-300">{Number(i.amount).toFixed(2)}€</span></span>
                      ))}
                    </span>
                  </span>
                ) : (
                  <span className="block text-zinc-500 text-xs">Não há prestações por pagar para redistribuir.</span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
              {recalcDialog.refund_needed > 0 ? (
                <AlertDialogAction className="btn-primary" onClick={() => { const d = recalcDialog; setRecalcDialog(null); handleSave({}); }}>
                  Mudar tipo (sem recalcular)
                </AlertDialogAction>
              ) : (
                <AlertDialogAction className="btn-primary" onClick={() => { const d = recalcDialog; setRecalcDialog(null); handleSave({ payment_schedule: d.new_schedule || undefined }); }}>
                  Confirmar e recalcular
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Dialog>
  );
};

// Sales Management
